import React, { useEffect, useMemo, useState } from "react";
import Layout from "../layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import LoadingOverlay from "react-loading-overlay-ts";
import Modal from "react-bootstrap/Modal";
import {
  listChecklistStages,
  createChecklistStage,
  updateChecklistStage,
  deleteChecklistStage,
  listChecklistTemplates,
  createChecklistTemplate,
  updateChecklistTemplate,
  deleteChecklistTemplate
} from "../Redux/Action/Checklist";

export default function ChecklistManager() {
  const dispatch = useDispatch();
  const { stages, templates } = useSelector((state) => state.Checklist);
  const [loading, setLoading] = useState(true);
  const [stageModalOpen, setStageModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [stageForm, setStageForm] = useState({
    name: "",
    displayName: "",
    description: "",
    order: 0,
    isLayover: false,
    isEnabled: true
  });
  const [templateForm, setTemplateForm] = useState({
    stageId: "",
    packageTypes: ["basic", "medium", "premium"],
    title: "",
    description: "",
    order: 0,
    isMandatory: true,
    requiresPhoto: false,
    requiresLocation: false,
    flightType: "both",
    isLayover: false,
    isEnabled: true
  });

  // Filters
  const [selectedStageFilter, setSelectedStageFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    Promise.all([
      dispatch(listChecklistStages()),
      dispatch(listChecklistTemplates())
    ]).finally(() => setLoading(false));
  }, [dispatch]);

  const stagesById = useMemo(() => {
    const map = {};
    (stages || []).forEach((s) => {
      map[s._id] = s;
    });
    return map;
  }, [stages]);

  const resetFilters = () => {
    setSelectedStageFilter("all");
    setSearchQuery("");
  };

  const templatesByStage = useMemo(() => {
    const grouped = {};
    (templates || []).forEach((t) => {
      // Search Filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const hit = t.title.toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q);
        if (!hit) return;
      }

      const key = t.stageId?._id || t.stageId;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(t);
    });
    Object.keys(grouped).forEach((k) => {
      grouped[k].sort((a, b) => a.order - b.order);
    });
    return grouped;
  }, [templates, searchQuery]);

  const filteredStages = useMemo(() => {
    let list = stages || [];
    if (selectedStageFilter !== "all") {
      list = list.filter((s) => s._id === selectedStageFilter);
    }
    // Also filter out stages if they have no templates visible? 
    // Usually user wants to see the stage even if empty, unless searching.
    // If searching, maybe hide empty stages?
    if (searchQuery) {
       // Check if stage matches OR has matching templates
       list = list.filter(s => {
         const stageMatch = s.displayName.toLowerCase().includes(searchQuery.toLowerCase());
         const hasTemplates = (templatesByStage[s._id] || []).length > 0;
         return stageMatch || hasTemplates;
       });
    }
    return list;
  }, [stages, selectedStageFilter, searchQuery, templatesByStage]);

  const openCreateStage = () => {
    setEditingStage(null);
    setStageForm({
      name: "",
      displayName: "",
      description: "",
      order: (stages || []).length,
      isLayover: false,
      isEnabled: true
    });
    setStageModalOpen(true);
  };

  const openEditStage = (stage) => {
    setEditingStage(stage);
    setStageForm({
      name: stage.name,
      displayName: stage.displayName,
      description: stage.description || "",
      order: stage.order || 0,
      isLayover: !!stage.isLayover,
      isEnabled: stage.isEnabled
    });
    setStageModalOpen(true);
  };

  const submitStage = () => {
    if (!stageForm.name || !stageForm.displayName) {
      alert("Please fill in all required fields (Internal Name, Display Name).");
      return;
    }
    setLoading(true);
    if (editingStage) {
      dispatch(
        updateChecklistStage({
          id: editingStage._id,
          data: {
            displayName: stageForm.displayName,
            description: stageForm.description,
            order: Number(stageForm.order) || 0,
            isLayover: !!stageForm.isLayover,
            isEnabled: !!stageForm.isEnabled
          }
        })
      ).finally(() => {
        setStageModalOpen(false);
        setLoading(false);
      });
    } else {
      dispatch(
        createChecklistStage({
          name: stageForm.name,
          displayName: stageForm.displayName,
          description: stageForm.description,
          order: Number(stageForm.order) || 0,
          isLayover: !!stageForm.isLayover,
          isEnabled: !!stageForm.isEnabled
        })
      ).finally(() => {
        setStageModalOpen(false);
        setLoading(false);
      });
    }
  };

  const removeStage = (stage) => {
    if (!window.confirm("Delete this stage? All linked templates remain but may be orphaned.")) return;
    setLoading(true);
    dispatch(deleteChecklistStage(stage._id)).finally(() => setLoading(false));
  };

  const openCreateTemplate = (stageId) => {
    setEditingTemplate(null);
    const parentStage = stagesById[stageId];
    setTemplateForm({
      stageId: stageId || "",
      packageTypes: ["basic", "medium", "premium"],
      title: "",
      description: "",
      order: ((templatesByStage[stageId] || []).length || 0) + 1,
      isMandatory: true,
      requiresPhoto: false,
      requiresLocation: false,
      flightType: "both",
      isLayover: parentStage?.isLayover || false,
      isEnabled: true
    });
    setTemplateModalOpen(true);
  };

  const openEditTemplate = (tpl) => {
    setEditingTemplate(tpl);
    let pkgTypes = [];
    if (tpl.packageTypes && Array.isArray(tpl.packageTypes)) {
      pkgTypes = tpl.packageTypes;
    } else if (tpl.packageType === "all") {
      pkgTypes = ["basic", "medium", "premium"];
    } else if (tpl.packageType) {
      pkgTypes = [tpl.packageType];
    } else {
      pkgTypes = ["basic", "medium", "premium"];
    }

    setTemplateForm({
      stageId: tpl.stageId?._id || tpl.stageId,
      packageTypes: pkgTypes,
      title: tpl.title,
      description: tpl.description || "",
      order: tpl.order || 0,
      isMandatory: !!tpl.isMandatory,
      requiresPhoto: !!tpl.requiresPhoto,
      requiresLocation: !!tpl.requiresLocation,
      flightType: tpl.flightType || "both",
      isLayover: !!tpl.isLayover,
      isEnabled: !!tpl.isEnabled
    });
    setTemplateModalOpen(true);
  };

  const submitTemplate = () => {
    if (!templateForm.stageId || !templateForm.title) {
      alert("Please fill in all required fields (Stage, Title).");
      return;
    }
    if (templateForm.packageTypes.length === 0) {
      alert("Please select at least one package type.");
      return;
    }
    setLoading(true);
    const data = {
      stageId: templateForm.stageId,
      packageTypes: templateForm.packageTypes,
      title: templateForm.title,
      description: templateForm.description,
      order: Number(templateForm.order) || 0,
      isMandatory: !!templateForm.isMandatory,
      requiresPhoto: !!templateForm.requiresPhoto,
      requiresLocation: !!templateForm.requiresLocation,
      flightType: templateForm.flightType,
      isLayover: !!templateForm.isLayover,
      isEnabled: !!templateForm.isEnabled
    };
    if (editingTemplate) {
      dispatch(
        updateChecklistTemplate({
          id: editingTemplate._id,
          data: {
            packageTypes: data.packageTypes,
            title: data.title,
            description: data.description,
            order: data.order,
            isMandatory: data.isMandatory,
            requiresPhoto: data.requiresPhoto,
            requiresLocation: data.requiresLocation,
            flightType: data.flightType,
            isLayover: data.isLayover,
            isEnabled: data.isEnabled
          }
        })
      ).finally(() => {
        setTemplateModalOpen(false);
        setLoading(false);
      });
    } else {
      dispatch(createChecklistTemplate(data)).finally(() => {
        setTemplateModalOpen(false);
        setLoading(false);
      });
    }
  };

  const removeTemplate = (tpl) => {
    if (!window.confirm("Delete this checklist item?")) return;
    setLoading(true);
    dispatch(deleteChecklistTemplate(tpl._id)).finally(() => setLoading(false));
  };

  const moveStage = async (index, direction) => {
    const sortedStages = (stages || []).slice().sort((a, b) => a.order - b.order);
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= sortedStages.length) return;

    // Swap in array
    const item = sortedStages[index];
    sortedStages.splice(index, 1);
    sortedStages.splice(newIndex, 0, item);

    setLoading(true);
    try {
      // Re-index all to ensure clean order (1, 2, 3...)
      const promises = sortedStages.map((s, i) => {
        if (s.order !== i + 1) {
          return dispatch(updateChecklistStage({ id: s._id, data: { order: i + 1 } }));
        }
        return Promise.resolve();
      });
      await Promise.all(promises);
      dispatch(listChecklistStages());
    } finally {
      setLoading(false);
    }
  };

  const moveTemplate = async (tpl, direction, allTemplates) => {
    // If filtering is active, disable reordering to avoid confusion
    if (selectedStageFilter !== "all" || searchQuery) {
       alert("Please clear filters before reordering templates.");
       return;
    }

    const sortedTemplates = allTemplates.slice().sort((a, b) => a.order - b.order);
    const index = sortedTemplates.findIndex((t) => t._id === tpl._id);
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= sortedTemplates.length) return;

    // Swap in array
    const item = sortedTemplates[index];
    sortedTemplates.splice(index, 1);
    sortedTemplates.splice(newIndex, 0, item);

    setLoading(true);
    try {
      // Re-index all
      const promises = sortedTemplates.map((t, i) => {
        if (t.order !== i + 1) {
          return dispatch(updateChecklistTemplate({ id: t._id, data: { order: i + 1 } }));
        }
        return Promise.resolve();
      });
      await Promise.all(promises);
      dispatch(listChecklistTemplates());
    } finally {
      setLoading(false);
    }
  };

  const renderStageRow = (stage, index, allStages) => {
    const key = stage._id;
    const stageTemplates = templatesByStage[key] || [];
    return (
      <div key={key} className="modern-user-section-card" style={{ marginBottom: 12, background: "#fff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, color: "#034328" }}>
              {stage.displayName} <span style={{ fontWeight: 400, fontSize: 12 }}>({stage.name})</span>
            </div>
            {stage.description ? <div style={{ fontSize: 12, color: "#6b7280" }}>{stage.description}</div> : null}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="common-btn"
              style={{ padding: "4px 8px", fontSize: 12 }}
              onClick={() => moveStage(index, -1)}
              disabled={index === 0}
            >
              <i className="fas fa-arrow-up"></i>
            </button>
            <button
              className="common-btn"
              style={{ padding: "4px 8px", fontSize: 12 }}
              onClick={() => moveStage(index, 1)}
              disabled={index === allStages.length - 1}
            >
              <i className="fas fa-arrow-down"></i>
            </button>
            <span style={{ fontSize: 12, color: stage.isEnabled ? "#16a34a" : "#b91c1c", alignSelf: "center" }}>
              {stage.isEnabled ? "Enabled" : "Disabled"}
            </span>
            {stage.isLayover && (
              <span style={{ fontSize: 12, color: "#d97706", alignSelf: "center", fontWeight: "bold" }}>
                Layover Stage
              </span>
            )}
            <button className="common-btn" onClick={() => openCreateTemplate(stage._id)}>
              + Add Step
            </button>
            <button className="common-btn" onClick={() => openEditStage(stage)}>
              Edit
            </button>
            <button className="common-btn" onClick={() => removeStage(stage)}>
              Delete
            </button>
          </div>
        </div>

        {stageTemplates.length > 0 && (
          <div style={{ marginTop: 10 }} className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th style={{ width: "24%", color: "#034328" }}>Title</th>
                  <th style={{ width: "10%", color: "#034328" }}>Order</th>
                  <th style={{ width: "10%", color: "#034328" }}>Package</th>
                  <th style={{ width: "12%", color: "#034328" }}>Mandatory</th>
                  <th style={{ width: "12%", color: "#034328" }}>Photo</th>
                  <th style={{ width: "12%", color: "#034328" }}>Location</th>
                  <th style={{ width: "12%", color: "#034328" }}>Flight</th>
                  <th style={{ width: "8%", color: "#034328" }}>Layover</th>
                  <th style={{ width: "8%", color: "#034328" }}>Enabled</th>
                  <th style={{ width: "12%", color: "#034328" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stageTemplates.map((tpl, tplIndex) => (
                  <tr key={tpl._id}>
                    <td>{tpl.title}</td>
                    <td>{tpl.order}</td>
                    <td>
                      {tpl.packageTypes && tpl.packageTypes.length > 0
                        ? tpl.packageTypes.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(", ")
                        : tpl.packageType || "All"}
                    </td>
                    <td>{tpl.isMandatory ? "Yes" : "No"}</td>
                    <td>{tpl.requiresPhoto ? "Yes" : "No"}</td>
                    <td>{tpl.requiresLocation ? "Yes" : "No"}</td>
                    <td>{tpl.flightType}</td>
                    <td>{tpl.isLayover ? "Yes" : "No"}</td>
                    <td>{tpl.isEnabled ? "Yes" : "No"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <i
                          className="fas fa-arrow-up"
                          style={{
                            cursor: tplIndex === 0 ? "not-allowed" : "pointer",
                            opacity: tplIndex === 0 ? 0.3 : 1
                          }}
                          onClick={() => tplIndex > 0 && moveTemplate(tpl, -1, stageTemplates)}
                        ></i>
                        <i
                          className="fas fa-arrow-down"
                          style={{
                            cursor: tplIndex === stageTemplates.length - 1 ? "not-allowed" : "pointer",
                            opacity: tplIndex === stageTemplates.length - 1 ? 0.3 : 1
                          }}
                          onClick={() =>
                            tplIndex < stageTemplates.length - 1 && moveTemplate(tpl, 1, stageTemplates)
                          }
                        ></i>
                        <i className="fas fa-pencil" onClick={() => openEditTemplate(tpl)}></i>
                        <i className="fas fa-trash" onClick={() => removeTemplate(tpl)}></i>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <LoadingOverlay active={loading} text="Loading checklist configuration...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">
            {/* Filter Bar */}
            <div className="modern-user-section-card" style={{ margin: 12, background: "#fff" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <button
                  className="common-btn"
                  style={{ background: selectedStageFilter === "all" ? "#034328" : "#e8f9f2", color: selectedStageFilter === "all" ? "#fff" : "#034328" }}
                  onClick={() => setSelectedStageFilter("all")}
                >
                  All Stages
                </button>
                {(stages || []).map((s) => (
                  <button
                    key={s._id}
                    className="common-btn"
                    style={{ background: selectedStageFilter === s._id ? "#034328" : "#e8f9f2", color: selectedStageFilter === s._id ? "#fff" : "#034328" }}
                    onClick={() => setSelectedStageFilter(s._id)}
                  >
                    {s.displayName}
                  </button>
                ))}
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ maxWidth: 280, marginLeft: "auto" }}
                />
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <button className="common-btn" onClick={resetFilters} style={{ marginLeft: "auto" }}>
                  Reset
                </button>
                <button className="common-btn" onClick={openCreateStage}>+ Add Stage</button>
              </div>
            </div>

            {/* Stages List */}
            <div className="section-wrapper" style={{ padding: 12 }}>
              {filteredStages.map((stage, index) => renderStageRow(stage, index, filteredStages))}
              {filteredStages.length === 0 && (
                <div style={{ padding: 20, textAlign: "center", color: "#666" }}>
                  No stages found matching your filters.
                </div>
              )}
            </div>
          </div>
        </div>

        <Modal show={stageModalOpen} onHide={() => setStageModalOpen(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{editingStage ? "Edit Stage" : "Create Stage"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Stage Form Inputs */}
            <div className="mb-3">
              <label className="form-label">Internal Name (Key)</label>
              <input
                type="text"
                className="form-control"
                value={stageForm.name}
                onChange={(e) => setStageForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. preDeparture"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Display Name</label>
              <input
                type="text"
                className="form-control"
                value={stageForm.displayName}
                onChange={(e) => setStageForm((f) => ({ ...f, displayName: e.target.value }))}
                placeholder="e.g. Pre-Departure Checks"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows={2}
                value={stageForm.description}
                onChange={(e) => setStageForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Order</label>
              <input
                type="number"
                className="form-control"
                value={stageForm.order}
                onChange={(e) => setStageForm((f) => ({ ...f, order: e.target.value }))}
              />
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="stageIsLayover"
                checked={stageForm.isLayover}
                onChange={(e) => setStageForm((f) => ({ ...f, isLayover: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="stageIsLayover">
                Is Layover Stage
              </label>
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="stageEnabled"
                checked={stageForm.isEnabled}
                onChange={(e) => setStageForm((f) => ({ ...f, isEnabled: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="stageEnabled">
                Enabled
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="common-btn" style={{ background: "#ccc", color: "#000" }} onClick={() => setStageModalOpen(false)}>
              Cancel
            </button>
            <button className="common-btn" onClick={submitStage}>
              Save Stage
            </button>
          </Modal.Footer>
        </Modal>
        
        <Modal show={templateModalOpen} onHide={() => setTemplateModalOpen(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{editingTemplate ? "Edit Checklist Step" : "Add Checklist Step"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Stage</label>
              <select
                className="form-control"
                value={templateForm.stageId}
                onChange={(e) => {
                  const sId = e.target.value;
                  const parent = stagesById[sId];
                  setTemplateForm((f) => ({
                    ...f,
                    stageId: sId,
                    isLayover: parent?.isLayover ? true : f.isLayover
                  }));
                }}
              >
                <option value="">Select stage</option>
                {(stages || [])
                  .slice()
                  .sort((a, b) => a.order - b.order)
                  .map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.displayName}
                    </option>
                  ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Title</label>
              <input
                type="text"
                className="form-control"
                value={templateForm.title}
                onChange={(e) => setTemplateForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Step title"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={templateForm.description}
                onChange={(e) => setTemplateForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Order</label>
              <input
                type="number"
                className="form-control"
                value={templateForm.order}
                onChange={(e) => setTemplateForm((f) => ({ ...f, order: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Package Types</label>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                {["basic", "medium", "premium"].map((pkg) => (
                  <label key={pkg} style={{ cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={templateForm.packageTypes.includes(pkg)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setTemplateForm((prev) => {
                          const current = prev.packageTypes || [];
                          if (checked) {
                            return { ...prev, packageTypes: [...current, pkg] };
                          } else {
                            return { ...prev, packageTypes: current.filter((p) => p !== pkg) };
                          }
                        });
                      }}
                    />{" "}
                    {pkg.charAt(0).toUpperCase() + pkg.slice(1)}
                  </label>
                ))}
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Flight Type</label>
              <select
                className="form-control"
                value={templateForm.flightType}
                onChange={(e) => setTemplateForm((f) => ({ ...f, flightType: e.target.value }))}
              >
                <option value="both">Both</option>
                <option value="domestic">Domestic</option>
                <option value="international">International</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Constraints</label>
              <div className="d-flex" style={{ gap: 16, flexWrap: "wrap" }}>
                <label>
                  <input
                    type="checkbox"
                    checked={templateForm.isMandatory}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, isMandatory: e.target.checked }))}
                  />{" "}
                  Mandatory
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={templateForm.requiresPhoto}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, requiresPhoto: e.target.checked }))}
                  />{" "}
                  Requires Photo
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={templateForm.requiresLocation}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, requiresLocation: e.target.checked }))}
                  />{" "}
                  Requires Location
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={templateForm.isLayover}
                    disabled={stagesById[templateForm.stageId]?.isLayover}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, isLayover: e.target.checked }))}
                  />{" "}
                  Is Layover
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={templateForm.isEnabled}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, isEnabled: e.target.checked }))}
                  />{" "}
                  Enabled
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button className="common-btn" onClick={submitTemplate}>
                Save
              </button>
            </div>
          </Modal.Body>
        </Modal>
      </LoadingOverlay>
    </Layout>
  );
}

